import { ReactNode } from 'react';
import { css } from '@emotion/react';
import { TimeFilterType } from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { useTranslation } from '@frontend/i18n';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Button, Heading, Text } from '@frontend/design-system';
import { useHasAdminAccess } from '../../hooks/use-has-admin-access';
import { BarChartDataResponse } from '../../hooks/use-time-based-bar-chart';
import { chartWithDataCardsStyles } from '../../styles';
import { DataCard } from '../data-card';
import { AnalyticsInfoPopover } from '../info-popover';
import { BarClickParams, TimeBasedBarChart } from '../time-based-bar-chart';
import { messagingRoiChartSeriesKeys, messagingRoiLabelMap, MessagingRoiConfig } from './messaging-roi-config';

const DEFAULT_X_AXIS = TimeFilterType.DAILY;

const COLORS: Record<string, string> = {
  engagementCount: theme.colors.success40,
  nonEngagementCount: theme.colors.warning20,
};

export interface MessagingROIChartViewProps {
  config: MessagingRoiConfig;
  chartData?: BarChartDataResponse;
  isLoadingChart: boolean;
  chartHasNoData: boolean;
  filterHintText?: string;
  selectedXAxis: TimeFilterType;
  defaultXAxis?: TimeFilterType;
  onXAxisChange: (xAxis: TimeFilterType) => void;
  onBarClick?: (params: BarClickParams) => void;
  /** Optional action rendered in the no-data state (e.g. a "Review Mapping" button). */
  noDataAction?: ReactNode;
}

/**
 * Presentational messaging ROI chart (stacked engaged / non-engaged bars with a data-card grid).
 *
 * All data fetching and store wiring lives in the consuming feature; this component is purely
 * driven by props so it can be reused across communication analytics and marketing bulk text.
 */
export const MessagingROIChartView = ({
  config,
  chartData,
  isLoadingChart,
  chartHasNoData,
  filterHintText,
  selectedXAxis,
  defaultXAxis = DEFAULT_X_AXIS,
  onXAxisChange,
  onBarClick,
  noDataAction,
}: MessagingROIChartViewProps) => {
  if (chartHasNoData) {
    return <NoDataMessage config={config} noDataAction={noDataAction} />;
  }

  return (
    <div css={chartWithDataCardsStyles.outerContainer}>
      <Heading as='span' level={3} css={chartWithDataCardsStyles.heading}>
        <span css={chartWithDataCardsStyles.headingLeft}>
          {config.chartTitle}
          <AnalyticsInfoPopover description={config.chartInfoTip} />
        </span>
        {filterHintText && <Text color='light'>{filterHintText}</Text>}
      </Heading>
      <div css={chartWithDataCardsStyles.innerWrapper}>
        <div css={chartWithDataCardsStyles.cardsGrid}>
          {config.cardsData.map((card) => (
            <DataCard key={card.label} {...card} />
          ))}
        </div>

        <TimeBasedBarChart
          data={chartData}
          isLoading={isLoadingChart}
          seriesConfig={{ mode: 'stacked', seriesKeys: [...messagingRoiChartSeriesKeys] }}
          preserveSeriesOrder
          colors={COLORS}
          labels={messagingRoiLabelMap}
          xAxis={{
            selected: selectedXAxis,
            defaultSelected: defaultXAxis,
            onChange: onXAxisChange,
          }}
          appearance={{
            mode: 'stacked',
            barRadius: 4,
            skipBarRadiusOnStackJoins: true,
          }}
          onBarClick={onBarClick}
          legends={{
            formatValue: formatters.value,
            doNotCollapseLegends: true,
          }}
          style={{ margin: 0 }}
        />
      </div>
    </div>
  );
};

MessagingROIChartView.displayName = 'MessagingROIChartView';

const NoDataMessage = ({ config, noDataAction }: { config: MessagingRoiConfig; noDataAction?: ReactNode }) => {
  const { t } = useTranslation('analytics');

  return (
    <section css={[chartWithDataCardsStyles.outerContainer, styles.noData]}>
      <Heading as='span' level={3} css={chartWithDataCardsStyles.heading}>
        <span css={chartWithDataCardsStyles.headingLeft}>
          {config.chartTitle}
          <AnalyticsInfoPopover description={config.chartInfoTip} />
        </span>
      </Heading>
      <div css={styles.noDataMessageInnerContainer}>
        <Text>
          {t('There is no data available for')}{' '}
          <Text as='span' weight='bold'>
            {config.chartTitle}
          </Text>{' '}
          {t('for the selected criteria.')}
          {!config.hideConfigureSettingsMsg && t('Please consider setting up the same from auto-message settings.')}
        </Text>
        {noDataAction ?? <ReviewMappingAction />}
      </div>
    </section>
  );
};
const ReviewMappingAction = () => {
  const { t } = useTranslation('analytics');
  const { navigate: settingsNavigate } = useSettingsNavigate();
  const hasAdminAccess = useHasAdminAccess();

  if (!hasAdminAccess) {
    return null;
  }

  return (
    <Button
      variant='tertiary'
      onClick={() => settingsNavigate({ to: '/messaging/auto-messages' })}
      trackingId='past-appointments-review-mapping-button'
    >
      {t('Review Mapping')}
    </Button>
  );
};

const styles = {
  noData: css`
    gap: ${theme.spacing(1)};
  `,
  noDataMessageInnerContainer: css`
    display: flex;
    gap: ${theme.spacing(1)};
    align-items: center;
  `,
};
